import Vue from 'vue';
import Vuex from 'vuex'
import axios from 'axios'
import _ from "lodash"
import mockDispatchRequestAllowedStatusChanges from "./data/mockDispatchRequestAllowedStatusChanges";


Vue.use(Vuex)

export default new Vuex.Store({
    state: {
        configPromise: null,
        config: {},

        userPromise: null,
        user: {},
        permissions: [],

        allLocations: [],
        dispatchRequestBatchTypes: [],
        dispatchTypes: {},
        dispatchRequestAllowedStatusChanges: {},
        cascadedDispatchRequestAllowedStatusChanges: {},
        paymentsAllowedMethodChanges: {},
        pausableStatuses: {},
        locationDataTypes: {},
        locationsForUser: [],
        productStates: [],
        returnReasons: [],
        dispatchInfoPerLocation: {},
        carrierInformation: [],
        paymentMethodsInformation: [],

        products: {},

        orderQuickSearchQuery: '',
    },
    mutations: {
        setOrderQuickSearchQuery(state, query) {
            state.orderQuickSearchQuery = query
        },
    },
    actions: {
        loadDispatchInfoForLocation: async ({context, state}, location) => {
            const locationHash = `${location.type}/${location.identifier}`
            if (!state.dispatchInfoPerLocation[locationHash]) {
                return axios.get(`/api/rest/locations/${location.type}/${location.identifier}/dispatch-types`)
                    .then(response => {
                        state.dispatchInfoPerLocation[locationHash] = response.data
                        return response.data
                    })
            }
            else {
                return Promise.resolve()
                    .then(() => {
                        return state.dispatchInfoPerLocation[locationHash]
                    })
            }
        },
    },
    getters: {
        getOmniPortalBaseURL: async state => {
            return await axios.get('/api/frontend/config')
                .then(response => {
                    state.omniPortalBaseURL = response.data.general?.['omni-portal-base-url'] || '#';
                    return state.omniPortalBaseURL;
                });
        },

        getUser: async state => {
            return axios.get('/api/current-user'+document.location.search) // This allows query params to pass to the backend (such as xdebug session id ;-))
                .then(response => {
                    state.user = response.data.user
                    state.permissions = response.data.permissions
                })
        },
        getConfig: async state => {
            return axios.get('/api/frontend/config')
                .then(response => {
                    state.config = response.data

                    const faviconElement = document.querySelector("link[rel~='icon']")
                    if (faviconElement !== null && state.config.general.environment !== 'production') {
                      faviconElement.href = '/favicon-grey.png'
                    }
                })
        },
        getAllLocations: async state => {
            return axios.get('/api/rest/locations', {
                params: {
                    'include_properties': ['supportsSalesChannels', 'orderPickupWithCodeEnabled'],
                }
            })
                .then(response => state.allLocations = response.data)
        },
        getLocationByIdentifier: (state, getters) => (locationIdentifier) => {
            return getters.getAllLocations
                .then(locations => locations.find(l => l.identifier === locationIdentifier))
        },
        getDispatchRequestAllowedStatusChanges: async (state) => {
            return axios.get(`/api/rest/dispatch-requests/allowed-status-changes`)
                .then(response => {
                    state.dispatchRequestAllowedStatusChanges = response.data.default
                    state.cascadedDispatchRequestAllowedStatusChanges = response.data.cascaded
                    state.pausableStatuses = response.data.pausable
                })
        },
        getPaymentsAllowedMethodChanges: async (state) => {
            return axios.get(`/api/rest/payments/allowed-method-changes`)
                .then(response => {
                    state.paymentsAllowedMethodChanges = response.data
                })
        },
        // Make sure to resolve this.getDispatchRequestAllowedStatusChanges() before calling this function!
        getFromStatusesForRerouteToStatus: state => (toStatus, forCascaded = false) => {
            let fromStatuses = []

            const allowedStatusChanges = (forCascaded)
                ? state.cascadedDispatchRequestAllowedStatusChanges
                : state.dispatchRequestAllowedStatusChanges

            _.each(allowedStatusChanges, (toStatuses, fromStatus) => {
                if (toStatuses?.indexOf(toStatus) !== -1) {
                    fromStatuses.push(fromStatus)
                }
            })
            return fromStatuses
        },
        getPausableStatuses: state => (locationTypeIdentifier) => {
            let returnStatuses = []
            _.each(state.pausableStatuses, (pausableStatuses, pausableLocationTypeIdentifier) => {
                if (locationTypeIdentifier === pausableLocationTypeIdentifier)
                {
                    returnStatuses = pausableStatuses
                }
            })
            return returnStatuses
        },
        getDispatchTypes: async state => {
            return axios.get('/api/rest/dispatch-types')
                .then(response => state.dispatchTypes = _.keyBy(response.data, 'identifier'))
        },
        getLocationDataTypes: async state => {
            return axios.get('/api/rest/location-data-types')
                .then(response => {
                    return state.locationDataTypes = response.data
                })
        },
        getLocationsForUser: async state => {
            return axios.get('/api/rest/locations', {
                params: {
                    'only_linked': 1,
                    'include_properties': [
                        'canProcessProducts',
                        'pickByStore.useNewProductProcessing', // Speciaal voor INT, wordt als het goed is binnenkort uitgefaseerd
                    ],
                }
            })
                .then(response => {
                    state.locationsForUser = response.data
                })
        },
        getProductStates: async state => {
            return axios.get('/api/rest/product-states')
                .then(response => {
                    state.productStates = response.data
                })
        },
        getReturnReasons: async state => {
            return axios.get('/api/rest/return-reasons')
                .then(response => {
                    state.returnReasons = response.data
                })
        },
        getDispatchRequestBatchTypes: async state => {
            return axios.get('/api/rest/dispatch-request-batch-types')
                .then(response => {
                    state.dispatchRequestBatchTypes = response.data
                })
        },
        getDispatchInfoForLocation: (state) => (locationType, locationIdentifier) => {
            const locationHash = `${locationType}/${locationIdentifier}`
            return state.dispatchInfoPerLocation[locationHash] ?? []
        },
        getCarrierInformation: async state => {
            return axios.get('/api/rest/carrier-management-services?include=active_methods.carrier')
                .then(response => {
                    state.carrierInformation = response.data
                })
        },
        getPaymentMethodsInformation: async state => {
            return axios.get('/api/rest/payment-service-providers?include=accounts.methods&is_refund_method=0')
                .then(response => {
                    state.paymentMethodsInformation = response.data
                })
        },

        carriersMethodOptions: state => {
            return _.chain(state.carrierInformation)
                .map(camase => camase.active_methods)
                .flatten()
                .uniqBy('id')
                .map(method => {
                    return {
                        id: method.id.toString(),
                        display_name: `${method.carrier.display_name} &rarr; ${method.display_name}`,
                    }
                })
                .orderBy('display_name')
                .value()
        },

        paymentMethodOptions: state => {
            let options = [];
            state.paymentMethodsInformation?.forEach(provider => {
                provider.accounts.forEach(account => {
                    account.methods.forEach(method => {
                        options.push({
                            id: method.id.toString(),
                            display_name: `${method.display_name}`,
                        })
                    })
                })
            })

            return _.orderBy(options, 'display_name')
        },

        getProductInfo: state => (productIdentifier) => {
            if (state.products[productIdentifier]) {
                return Promise.resolve()
            }

            return axios.get(`/api/rest/products/${productIdentifier}?include=attribute_values.product_attribute`)
                .then(response => {
                    state.products[productIdentifier] = response.data
                })
        },

        /**
         * Combines all front-end parameters from config into one object,
         * ordered by group, in order to make it more easily accessible.
         * @returns {Object} frontendParameters
         */
        frontendParameters: (state, getters) => {
            const data = state.config?.settings;

            // Check if data exists
            if (!data) {
                return {};
            }

            // Get the keys from the data that start with 'frontend-parameters'
            const frontendParameterKeys = Object.keys(data).filter(key => key.startsWith('frontend-parameters'));

            // Process and put into a single object, extracting the corresponding values from the data
            return frontendParameterKeys.reduce(
                (acc, key) =>
                {
                    const groupName = key.split('.')[1]; // Extract the group name, e.g., 'group-one' from 'frontend-parameters.group-one'

                    // Add the group to the accumulator, e.g. { "group-one": {...}, "group-two": {...} }
                    acc[groupName] = data[key];
                    return acc;
                },
                {}
            );
        },

        getFrontendParameterValues: (state, getters) => (group) => {
            const parameters = getters.frontendParameters;
            if (!group) {
                return parameters;
            }
            return parameters[group] || {};
        },

        getFrontendParameterValue: (state, getters) => (group, key) => {
            const keys = key.split('.')
            let value = getters.getFrontendParameterValues(group)
            try {
                for (let i in keys) {
                    value = value[keys[i]]
                }

                return value
            } catch (e) {
                console.error(`Key '${key}' not found in group '${group}'`)
            }
            return null;
        },
    }
})
